import React from 'react'

import CurrencyLogo from '@/components/CurrencyLogo'
import { nutrimaticLabels } from '@/nutrimatic'
import { cn } from '@/lib/utils'
import { Token } from '@/sdk'
import { useAddUserToken } from '@/state/user/hooks'

const labels = nutrimaticLabels.mobileSimple

export function isContractDiscoveryActive(
  importedByAddress: boolean,
  searchToken: Token | null | undefined,
): searchToken is Token {
  return importedByAddress && searchToken instanceof Token
}

/** Vista dedicada al resolver un contrato fuera del catálogo Don't Panic. */
export function TokenContractDiscoveryView({
  token,
  onImport,
  onCancel,
  variant = 'nutrimatic',
  className,
}: {
  token: Token
  onImport: (token: Token) => void
  onCancel: () => void
  variant?: 'nutrimatic' | 'compact'
  className?: string
}) {
  const addToken = useAddUserToken()
  const symbol = token.symbol ?? '—'
  const name = token.name ?? symbol

  const handleImport = () => {
    addToken(token)
    onImport(token)
  }

  if (variant === 'compact') {
    return (
      <div
        className={cn(
          'rounded-xl border border-dashed border-primary/35 bg-primary/5 p-4',
          className,
        )}
      >
        <p className="text-[10px] font-semibold uppercase tracking-wider text-primary/80">
          {labels.pickerContractFoundTitle}
        </p>
        <div className="mt-3 flex items-start gap-3">
          <CurrencyLogo currency={token} size="40px" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold">{symbol}</p>
            <p className="truncate text-xs text-muted-foreground">{name}</p>
            <p className="mt-1 break-all font-mono text-[10px] text-muted-foreground">{token.address}</p>
            <p className="mt-1 text-[10px] text-muted-foreground">
              {labels.pickerContractDecimals(token.decimals)}
            </p>
          </div>
        </div>
        <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{labels.pickerContractFoundLead}</p>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{labels.pickerContractFoundHint}</p>
        <div className="mt-4 flex flex-col gap-2">
          <button
            type="button"
            onClick={handleImport}
            className="rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
          >
            {labels.pickerContractImport}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-border px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-muted/40"
          >
            {labels.pickerCancelSearch}
          </button>
        </div>
      </div>
    )
  }

  return (
    <section className={cn('nm-mobile-simple__picker-discovery', className)}>
      <p className="nm-mobile-simple__picker-discovery-kicker">{labels.pickerContractFoundTitle}</p>

      <div className="nm-mobile-simple__picker-discovery-card">
        <div className="nm-mobile-simple__picker-discovery-identity">
          <CurrencyLogo currency={token} size="48px" />
          <div className="nm-mobile-simple__picker-discovery-copy">
            <p className="nm-mobile-simple__picker-discovery-symbol">{symbol}</p>
            <p className="nm-mobile-simple__picker-discovery-name">{name}</p>
          </div>
        </div>

        <dl className="nm-mobile-simple__picker-discovery-meta">
          <div className="nm-mobile-simple__picker-discovery-meta-row">
            <dt>{labels.pickerContractAddressLabel}</dt>
            <dd className="font-mono">{token.address}</dd>
          </div>
          <div className="nm-mobile-simple__picker-discovery-meta-row">
            <dt>{labels.pickerContractDecimalsLabel}</dt>
            <dd>{token.decimals}</dd>
          </div>
          <div className="nm-mobile-simple__picker-discovery-meta-row">
            <dt>{labels.pickerContractCatalogLabel}</dt>
            <dd>{labels.pickerNotInCatalog}</dd>
          </div>
        </dl>
      </div>

      <p className="nm-mobile-simple__picker-discovery-lead">{labels.pickerContractFoundLead}</p>
      <p className="nm-mobile-simple__picker-discovery-hint">{labels.pickerContractFoundHint}</p>

      <div className="nm-mobile-simple__picker-discovery-actions">
        <button type="button" className="nm-mobile-simple__picker-discovery-import" onClick={handleImport}>
          {labels.pickerContractImport}
        </button>
        <button type="button" className="nm-mobile-simple__picker-discovery-cancel" onClick={onCancel}>
          {labels.pickerCancelSearch}
        </button>
      </div>
    </section>
  )
}
